/* ==========================================================================
   Avalanche — cart drawer + cart page behaviour
   <av-line-quantity line="n">   quantity stepper + remove link for one cart line
   <av-cart-note>                saves the order note (drawer panel toggle + cart page textarea)
   <av-free-shipping-bar>        keeps the free shipping message in sync with cart:change
   <av-shipping-estimator>       country / province selects + shipping rates lookup
   ========================================================================== */

const T = () => window.Theme || {};
const config = () => T().config || {};
const cartRoot = () => config().routes?.cart || '/cart';
const define = (name, ctor) => !customElements.get(name) && customElements.define(name, ctor);

/* Line errors are shown next to the line, so the generic toast is skipped while a line change runs */
let pendingLineChanges = 0;
window.addEventListener('cart:error', (e) => {
  if (pendingLineChanges > 0) e.stopPropagation();
}, true);

/* ---------------------------------------------------------------------------
   Quantity / remove for one line (drawer + cart page)
   ------------------------------------------------------------------------- */
class LineQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-line-input]');
    this.onInputChange = () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(Number(this.input.value)), 350);
    };
    this.input?.addEventListener('change', this.onInputChange);
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.querySelector('[data-line-remove]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.update(0);
    });
  }

  disconnectedCallback() { clearTimeout(this.timer); }

  get scope() { return this.closest('[data-line-scope]') || this; }

  async update(quantity) {
    const { Cart } = T();
    const line = Number(this.getAttribute('line'));
    if (!Cart || !line || Number.isNaN(quantity) || quantity < 0) return;

    const scope = this.scope;
    const errorEl = scope.querySelector('[data-line-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    scope.classList.add('is-updating');
    pendingLineChanges++;

    try {
      await Cart.change(line, quantity);
      // On success the section is re-rendered and this element is replaced.
    } catch (err) {
      scope.classList.remove('is-updating');
      if (errorEl) {
        errorEl.textContent = err.message || config().strings?.cartError || '';
        errorEl.hidden = false;
      }
      // Restore the last quantity shown by the server
      if (this.input) {
        this.input.value = this.input.defaultValue;
        this.input.closest('av-quantity')?.refresh?.();
      }
    } finally {
      pendingLineChanges = Math.max(0, pendingLineChanges - 1);
    }
  }
}

/* ---------------------------------------------------------------------------
   Order note. In the drawer it lives in a panel opened by [data-note-toggle].
   The note is saved without re-rendering, so typing is never interrupted.
   ------------------------------------------------------------------------- */
class CartNote extends HTMLElement {
  connectedCallback() {
    this.field = this.querySelector('[name="note"]');
    this.field?.addEventListener('change', () => this.save());
    this.querySelector('[data-note-save]')?.addEventListener('click', () => {
      this.save();
      this.close();
    });
    this.toggle = this.id ? document.querySelector(`[data-note-toggle][aria-controls="${CSS.escape(this.id)}"]`) : null;
    this.onToggle = (e) => {
      e.preventDefault();
      this.classList.contains('is-open') ? this.close() : this.open();
    };
    this.toggle?.addEventListener('click', this.onToggle);
    this.onKey = (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        e.stopPropagation();
        this.close();
      }
    };
    this.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() { this.toggle?.removeEventListener('click', this.onToggle); }

  open() {
    this.classList.add('is-open');
    this.removeAttribute('inert');
    this.toggle?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.field?.focus({ preventScroll: true }));
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.setAttribute('inert', '');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.toggle?.focus({ preventScroll: true });
  }

  save() {
    if (!this.field || this.field.value === this.lastSaved) return;
    this.lastSaved = this.field.value;
    fetch(`${config().routes?.cartUpdate || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: this.field.value }),
      keepalive: true
    }).catch(() => {});
  }
}

/* ---------------------------------------------------------------------------
   Free shipping message
   ------------------------------------------------------------------------- */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.onCartChange = (e) => {
      const cart = e.detail?.cart;
      if (!cart?.items) return;
      const items = cart.items.filter((item) => item.requires_shipping).reduce((sum, item) => sum + item.final_line_price, 0);
      const discounts = (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + d.total_allocated_amount, 0);
      this.setAttribute('total', items - discounts);
      this.render();
    };
    document.addEventListener('cart:change', this.onCartChange);
  }

  disconnectedCallback() { document.removeEventListener('cart:change', this.onCartChange); }

  render() {
    const message = this.querySelector('[data-message]');
    if (!message) return;
    const remaining = Number(this.getAttribute('threshold')) - Number(this.getAttribute('total'));
    if (remaining <= 0) {
      message.innerHTML = this.dataset.reached;
    } else {
      const amount = T().formatMoney ? T().formatMoney(remaining) : (remaining / 100).toFixed(2);
      message.innerHTML = this.dataset.unreached.replace('[[amount]]', amount);
    }
  }
}

/* ---------------------------------------------------------------------------
   Shipping estimator
   ------------------------------------------------------------------------- */
const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class ShippingEstimator extends HTMLElement {
  connectedCallback() {
    this.country = this.querySelector('[data-country]');
    this.province = this.querySelector('[data-province]');
    this.provinceField = this.querySelector('[data-province-field]');
    this.zip = this.querySelector('[data-zip]');
    this.button = this.querySelector('[data-estimate]');
    this.results = this.querySelector('[data-results]');
    if (!this.country) return;

    const preset = this.getAttribute('country');
    if (preset) {
      const index = [...this.country.options].findIndex((o) => o.textContent.trim() === preset || o.value === preset);
      if (index > -1) this.country.selectedIndex = index;
    }
    this.country.addEventListener('change', () => this.fillProvinces());
    this.fillProvinces();

    this.button?.addEventListener('click', (e) => {
      e.preventDefault();
      this.estimate();
    });
    this.zip?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.estimate();
      }
    });
  }

  fillProvinces() {
    const option = this.country.options[this.country.selectedIndex];
    let provinces = [];
    try { provinces = JSON.parse(option?.dataset.provinces || '[]'); } catch (_) { /* malformed */ }
    this.provinceField.hidden = provinces.length === 0;
    this.province.replaceChildren();
    const preset = this.getAttribute('province');
    provinces.forEach(([code, name]) => {
      const selected = preset === code || preset === name;
      this.province.add(new Option(name, code, selected, selected));
    });
  }

  params() {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', this.zip?.value.trim() || '');
    params.set('shipping_address[country]', this.country.value);
    params.set('shipping_address[province]', this.provinceField.hidden ? '' : this.province.value);
    return params.toString();
  }

  async estimate() {
    if (this.button?.classList.contains('is-loading')) return;
    const { loadingBar } = T();
    const strings = config().strings || {};
    const query = this.params();
    this.button?.classList.add('is-loading');
    this.button?.setAttribute('aria-busy', 'true');
    loadingBar?.start();

    try {
      const prepare = await fetch(`${cartRoot()}/prepare_shipping_rates.json?${query}`, { method: 'POST' });
      if (!prepare.ok) {
        const errors = await prepare.json().catch(() => ({}));
        this.showErrors(errors, strings.shippingEstimatorError);
      } else {
        const rates = await this.poll(query);
        this.showRates(rates, strings);
      }
    } catch (_) {
      this.showErrors({}, strings.shippingEstimatorError);
    } finally {
      this.results.hidden = false;
      this.button?.classList.remove('is-loading');
      this.button?.removeAttribute('aria-busy');
      loadingBar?.done();
    }
  }

  /* Shopify computes rates asynchronously: the endpoint answers "null" until they are ready */
  async poll(query, attempt = 0) {
    const response = await fetch(`${cartRoot()}/async_shipping_rates.json?${query}`);
    const text = await response.text();
    if (text === 'null' || text.trim() === '') {
      if (attempt > 20) return [];
      await wait(500);
      return this.poll(query, attempt + 1);
    }
    return JSON.parse(text).shipping_rates || [];
  }

  showRates(rates, strings) {
    const heading = rates.length === 0 ? strings.shippingEstimatorNoResults : rates.length === 1 ? strings.shippingEstimatorOneResult : strings.shippingEstimatorMultipleResults;
    const list = rates.map((rate) => `<li>${escapeHTML(rate.presentment_name || rate.name)}: ${escapeHTML(rate.currency)} ${escapeHTML(rate.price)}</li>`).join('');
    this.results.innerHTML = `<div class="v-stack gap-2"><p>${escapeHTML(heading || '')}</p>${list ? `<ul role="list">${list}</ul>` : ''}</div>`;
  }

  showErrors(errors, heading) {
    const list = Object.entries(errors || {}).map(([key, value]) => `<li>${escapeHTML(key)} ${escapeHTML([].concat(value).join(', '))}</li>`).join('');
    this.results.innerHTML = `<div class="v-stack gap-2 text-error"><p>${escapeHTML(heading || '')}</p>${list ? `<ul role="list">${list}</ul>` : ''}</div>`;
  }
}

define('av-line-quantity', LineQuantity);
define('av-cart-note', CartNote);
define('av-free-shipping-bar', FreeShippingBar);
define('av-shipping-estimator', ShippingEstimator);

/* ---------------------------------------------------------------------------
   Theme editor: open the cart drawer while its section is selected
   ------------------------------------------------------------------------- */
if (window.Shopify?.designMode && !window.__avCartEditorHooks) {
  window.__avCartEditorHooks = true;
  const drawerIn = (target) => target?.querySelector?.('#cart-drawer');
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = drawerIn(e.target);
    if (drawer) customElements.whenDefined('av-overlay').then(() => drawer.open?.());
  });
  document.addEventListener('shopify:section:deselect', (e) => drawerIn(e.target)?.close?.());
  document.addEventListener('shopify:section:load', (e) => {
    const drawer = drawerIn(e.target);
    if (drawer) customElements.whenDefined('av-overlay').then(() => drawer.open?.());
  });
}
